import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Home, FileSearch, Sparkles, BookOpen, Settings, LogOut, User, Menu, X, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { adminApi } from '@/services/api/admin'
import TextBuddyLogo from '@/assets/logos/TextBuddy_Transparent_2.png'

interface NavbarProps {
  user?: {
    name: string
    email: string
    role: string
  } | null
  onLogout?: () => void
}

// Navigation items with optional role restriction
const navItems = [
  { path: '/', label: 'Home', icon: Home, adminOnly: false },
  { path: '/evaluate', label: 'Evaluate', icon: FileSearch, adminOnly: true },
  { path: '/enhance', label: 'Enhance', icon: Sparkles, adminOnly: false },
  { path: '/ai-summary', label: 'AI Summary', icon: BookOpen, adminOnly: true },
]

// Cross-link to the Founda Docs admin view. VITE_FOUNDA_DOCS_URL is injected per
// environment by the CI workflow (dev/uat/prod) and can be overridden in .env for
// local development. ?view=admin is read on first render by the Founda Docs App.
const FOUNDA_DOCS_ADMIN_URL =
  (import.meta.env.VITE_FOUNDA_DOCS_URL ||
    'https://app-foundaai-prod-fd-frontend.azurewebsites.net') + '/?view=admin'

export function Navbar({ user, onLogout }: NavbarProps) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Check if user is admin/superuser
  const isAdmin = user && (user.role === 'Admin' || user.role === 'SuperUser')

  // Fetch pending access requests count (only for admins)
  const { data: pendingRequests } = useQuery({
    queryKey: ['pendingRequests', 'count'],
    queryFn: () => adminApi.getPendingRequests(),
    enabled: !!isAdmin,
    refetchInterval: 60000, // Refresh every minute
  })

  // Fetch pending feedback count (only for admins)
  const { data: feedbackStats } = useQuery({
    queryKey: ['feedbackStats', 'navbar'],
    queryFn: () => adminApi.getFeedbackStats(),
    enabled: !!isAdmin,
    refetchInterval: 60000, // Refresh every minute
  })

  // Combined count: pending requests + pending feedback
  const pendingRequestsCount = pendingRequests?.length || 0
  const pendingFeedbackCount = feedbackStats?.pending || 0
  const totalPendingCount = pendingRequestsCount + pendingFeedbackCount

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter((item) => {
    if (item.adminOnly) {
      return user && (user.role === 'Admin' || user.role === 'SuperUser')
    }
    return true
  })

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex h-14 sm:h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src={TextBuddyLogo}
            alt="TextBuddy"
            className="h-10 sm:h-14 w-auto"
          />
        </Link>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center gap-1">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path
            const Icon = item.icon
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant={isActive ? 'default' : 'ghost'}
                  size="sm"
                  className={cn(
                    'gap-2',
                    isActive && 'bg-primary text-primary-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Button>
              </Link>
            )
          })}

          {/* Admin links - only for admin/superuser */}
          {isAdmin && (
            <>
              <Link to="/admin" className="relative">
                <Button
                  variant={location.pathname === '/admin' ? 'default' : 'ghost'}
                  size="sm"
                  className="gap-2"
                >
                  <Settings className="h-4 w-4" />
                  TB Admin
                </Button>
                {totalPendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm">
                    {totalPendingCount > 9 ? '9+' : totalPendingCount}
                  </span>
                )}
              </Link>
              <a
                href={FOUNDA_DOCS_ADMIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                title="Opens Founda Docs admin in a new tab"
              >
                <Button variant="ghost" size="sm" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  FD Admin
                </Button>
              </a>
            </>
          )}
        </div>

        {/* Desktop User Menu */}
        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <div className="flex items-center gap-3">
              <div className="hidden lg:block text-right">
                <p className="text-sm font-medium">{user.name}</p>
                <p className="text-xs text-muted-foreground">{user.role}</p>
              </div>
              <Avatar className="h-9 w-9 bg-primary">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {getInitials(user.name)}
                </AvatarFallback>
              </Avatar>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="gap-2">
              <User className="h-4 w-4" />
              Sign In
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-2">
          {user && (
            <Avatar className="h-8 w-8 bg-primary">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="h-9 w-9"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-background">
          <div className="px-4 py-3 space-y-2">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block"
                >
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    className={cn(
                      'w-full justify-start gap-3',
                      isActive && 'bg-primary text-primary-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Button>
                </Link>
              )
            })}

            {/* Admin links in mobile menu */}
            {isAdmin && (
              <>
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block relative"
                >
                  <Button
                    variant={location.pathname === '/admin' ? 'default' : 'ghost'}
                    className="w-full justify-start gap-3"
                  >
                    <Settings className="h-4 w-4" />
                    TB Admin
                    {totalPendingCount > 0 && (
                      <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {totalPendingCount > 9 ? '9+' : totalPendingCount}
                      </span>
                    )}
                  </Button>
                </Link>
                <a
                  href={FOUNDA_DOCS_ADMIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block"
                >
                  <Button variant="ghost" className="w-full justify-start gap-3">
                    <ExternalLink className="h-4 w-4" />
                    FD Admin
                  </Button>
                </a>
              </>
            )}

            {/* User info & logout in mobile */}
            {user && (
              <div className="pt-3 mt-3 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.role}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setMobileMenuOpen(false)
                      onLogout?.()
                    }}
                    className="gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
